import { useQuery } from "@tanstack/react-query"
import { buildUrl } from "@/SX/api"
import { calculateOdds } from "@/SX/hooks/useSXMarketData"
import useChainProviderHook from "@/hooks/useChainProvider"
import type { SxTicketItem } from "@/contexts/DetailedSxBetslipContext"

interface TicketAmmQuoteParams {
  currentTicket: SxTicketItem[]
  enabled?: boolean
  staleTime?: number
  gcTime?: number
  retry?: number
}

interface TicketAmmQuoteResult {
  liquidity: number
  allOdds: Array<{
    odd: number
    takerBetAmount: number
  }>
}

/**
 * Custom hook for fetching ticket AMM quotes using React Query
 * Provides automatic caching, refetching, and error handling
 */
export const useTicketAmmQuote = ({
  currentTicket,
  enabled = true,
  staleTime = 1000 * 60 * 5, // 5 minutes
  gcTime = 1000 * 60 * 10, // 10 minutes
  retry = 2,
}: TicketAmmQuoteParams) => {
  const { chainId, isSXChain } = useChainProviderHook()

  // Create a stable query key based on ticket data
  const queryKey = [
    "ticket-amm-quote",
    chainId,
    currentTicket.map(item => ({
      marketHash: item.marketHash,
      baseToken: item.baseToken,
      isMakerBettingOutcomeOne: item.isMakerBettingOutcomeOne,
    })),
  ]

  return useQuery({
    queryKey,
    queryFn: async (): Promise<TicketAmmQuoteResult> => {
      if (!isSXChain) {
        console.warn(
          `SX Betslip: Cannot fetch quote - chain ${chainId} not supported`
        )
        return { liquidity: 0, allOdds: [] }
      }

      if (!currentTicket || currentTicket.length === 0) {
        return { liquidity: 0, allOdds: [] }
      }

      const currentTicketItem = currentTicket[0]
      const baseToken = currentTicketItem.baseToken
      const marketHashes = currentTicketItem.marketHash

      const url = buildUrl(`orders`, {
        marketHashes,
        baseToken,
        orderStatus: "ACTIVE",
        chainId: chainId.toString(),
      })

      const response = await fetch(url)
      const data = await response.json()

      const isMakerBettingOutcomeOne =
        currentTicketItem.isMakerBettingOutcomeOne

      const orders = data.data.filter(
        (order: any) =>
          order.isMakerBettingOutcomeOne === isMakerBettingOutcomeOne
      )

      function sumTotalBetSize(orders: any[]): number {
        return orders.reduce((sum, order) => {
          const amount =
            ((Number(order.totalBetSize) * 10 ** 20) /
              Number(order.percentageOdds) -
              Number(order.totalBetSize)) /
            10 ** 6
          return sum + amount
        }, 0)
      }

      const liquidity = sumTotalBetSize(orders)

      const allOdds = orders
        .map((order: any) => ({
          odd: calculateOdds(order.percentageOdds, 10),
          takerBetAmount:
            ((Number(order.totalBetSize) * 10 ** 20) /
              Number(order.percentageOdds) -
              Number(order.totalBetSize)) /
            10 ** 6,
        }))
        .sort((a, b) => b.odd - a.odd)

      return { liquidity, allOdds }
    },
    enabled: enabled && isSXChain && currentTicket.length > 0,
    staleTime,
    gcTime,
    retry,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 5, // 5 minutes
  })
}

/**
 * Helper hook for getting just the liquidity value
 */
export const useTicketLiquidity = (params: TicketAmmQuoteParams) => {
  const query = useTicketAmmQuote(params)
  return {
    ...query,
    liquidity: query.data?.liquidity ?? 0,
  }
}

/**
 * Helper hook for getting just the odds data
 */
export const useTicketOdds = (params: TicketAmmQuoteParams) => {
  const query = useTicketAmmQuote(params)
  return {
    ...query,
    allOdds: query.data?.allOdds ?? [],
  }
}
